import { ContractTransactionResponse, formatUnits, parseUnits } from "ethers";
import { createContext, useContext, useEffect, useState } from "react";

import { useAuth } from "context/AuthContext/AuthProvider";
import {
  addUserTransaction,
  claimBridgeToken,
  getBridgeTokenAllowance,
  getBridgeTokenBalance,
  getBridgeTokens,
  getClientKey,
  getCurrentBlocks,
} from "lib/apis/bridge";
import useWallet from "lib/wallets/useWallet";

import { ARB_CHAINID, chainIds, DEFAULT_NATIVE_TOKEN_CONTRACT, targetBlockNumber } from "./constants";
import { anyBridgeAssist, anyToken } from "./contracts/use-contracts";
import { _getSymbol } from "./helpers";
import { AssetChainToken, AssetChainTransaction } from "../apis/bridge/index.dto";
import { ChainId } from "../node/types";

type TokenBalance = {
  [key in ChainId]: {
    bridgeAssist: number;
    user: number;
    symbol: string;
    amountCanBridge: number;
    chainId: ChainId;
  }[];
};
export type TokenDecimal = {
  [key in ChainId]: { symbol: string; decimal: bigint }[];
};

interface BridgeState {
  tokens: AssetChainToken[];
  balances: TokenBalance;
  decimals: TokenDecimal;
  loadingBalances: boolean;
  clientKey: string | null;
  getTokens: (chainIds: string[]) => Promise<void>;
  hasAllowance: (_chainId: ChainId, amount: number, symbol: string) => Promise<boolean>;
  getBalances: (chainIds?: ChainId[]) => Promise<void>;
  approve: (_chainId: ChainId, amount: number, symbol: string) => Promise<ContractTransactionResponse | null>;
  send: (amount: number, from: string, symbol: string) => Promise<AssetChainTransaction>;
  confirmBlock: (transaction: AssetChainTransaction) => Promise<void>;
  claimToken: (transaction: AssetChainTransaction) => Promise<string>;
}

const BridgeContext = createContext<BridgeState | null>(null);

export const useBridge = () => {
  const context = useContext(BridgeContext);
  if (!context) {
    throw new Error("useBridge must be used within a BridgeProvider");
  }
  return context;
};

export const BridgeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [clientKey, setClientKey] = useState<string | null>(null);
  const [tokens, setTokens] = useState<AssetChainToken[]>([]);
  const [balances, setBalances] = useState<TokenBalance>({} as TokenBalance);
  const [decimals, setDecimals] = useState<TokenDecimal>({} as TokenDecimal);
  const [loadingBalances, setloadingBalances] = useState(false);

  const { account, signer } = useWallet();
  const { isAuthenticated, signIn } = useAuth();

  useEffect(() => {
    getTokens();
  }, [clientKey]);

  // useEffect(() => {
  //   getBalances();
  // }, [account]);

  useEffect(() => {
    getClient();
  }, []);

  async function getClient() {
    try {
      const { data } = await getClientKey();
      setClientKey(data);
    } catch (error) {
      console.error(error);
    }
  }

  async function getTokens() {
    try {
      if (!clientKey) return;
      const { data: tokens } = await getBridgeTokens(clientKey);
      const _tokens: AssetChainToken[] = [];
      const _decimals = {} as TokenDecimal;

      await Promise.all(
        tokens.map(async (t) => {
          let symbol = t.symbol.toUpperCase();
          if (symbol === "USD₮0" || symbol === "USDT0") symbol = "USDT";
          if (symbol === "RWA" || symbol.toLowerCase() === "xRWA".toLowerCase()) symbol = "XRWA";

          const chainId = t.chainId as ChainId;
          if (!_decimals[chainId]) _decimals[chainId] = [];

          _decimals[chainId].push({
            decimal: BigInt(t.decimal),
            symbol,
          });

          _tokens.push(t);
        })
      );

      const dec = { ...decimals, ..._decimals };
      setDecimals(dec);
      setTokens(_tokens);
    } catch (error: any) {
      console.log(error);
    }
  }

  async function getBalances(_chains?: ChainId[]) {
    if (!clientKey) return;
    const _balances = {} as TokenBalance;
    if (!account) return;
    if (!decimals) return;

    setloadingBalances(true);

    const realChains = _chains && _chains.length > 0 ? _chains : chainIds;
    try {
      const realTokens = tokens
        .filter((t) => realChains.includes(t.chainId as ChainId))
        .map((t) => ({
          tokenAddress: t.tokenAddress,
          chainId: t.chainId,
          bridgeAssist: t.bridgeInfos[0].bridgeAddress,
          checkAmountCanBridge: t.chainId === ARB_CHAINID && t.symbol === "WNT",
          targetBlockTag: targetBlockNumber,
          symbol: t.symbol,
        }));

      if (realTokens.length <= 0) {
        setloadingBalances(false);
        return;
      }
      const { data: balancesFromApi } = await getBridgeTokenBalance(
        {
          evmAddress: account ?? "",
          solanaAddress: "",
          tokens: realTokens,
        },
        clientKey
      );

      Object.keys(balancesFromApi).forEach((chainId) => {
        const chainBalancesFromApi = balancesFromApi[chainId];
        const chainDecimals = decimals[chainId as ChainId];
        if (!chainDecimals) return;

        let chainBalances = _balances[chainId as ChainId];
        chainBalancesFromApi.forEach((b) => {
          let symbol = b.symbol;
          if (symbol === "USD₮0" || symbol === "USDT0") symbol = "USDT";
          if (symbol === "RWA" || symbol.toLowerCase() === "xRWA".toLowerCase()) symbol = "XRWA";
          if (!chainBalances) {
            chainBalances = [];
            _balances[chainId as ChainId] = [...chainBalances];
          }

          const tokenDecimal = chainDecimals.find((d) => d.symbol === symbol);
          if (!tokenDecimal) return;

          const chainBalanceIndex = chainBalances.findIndex((b) => b.symbol === symbol);
          if (chainBalanceIndex !== -1) {
            const chainBalance = chainBalances[chainBalanceIndex];
            chainBalance.user = +formatUnits(b.userBalance, tokenDecimal.decimal);
            chainBalance.bridgeAssist = +formatUnits(b.bridgeBalance, tokenDecimal.decimal);
            chainBalance.amountCanBridge = +formatUnits(b.amountCanBridge, tokenDecimal.decimal);
            chainBalance.chainId = chainId as ChainId;
            chainBalances[chainBalanceIndex] = chainBalance;
            _balances[chainId as ChainId] = chainBalances;
            // updat
          } else {
            _balances[chainId as ChainId].push({
              user: +formatUnits(b.userBalance, tokenDecimal.decimal),
              bridgeAssist: +formatUnits(b.bridgeBalance, tokenDecimal.decimal),
              symbol,
              amountCanBridge: +formatUnits(b.amountCanBridge, tokenDecimal.decimal),
              chainId: chainId as ChainId,
            });
          }
        });
      });
      setBalances(_balances);
      setloadingBalances(false);
    } catch (error: any) {
      console.log(error, "error");
      setloadingBalances(false);
    }
  }

  async function hasAllowance(_chainId: ChainId, amount: number, symbol: string) {
    try {
      if (!clientKey) return false;
      if (_chainId === "sol.devnet" || _chainId === "sol.mainnet") {
        return true;
      }
      if (!account) return false;
      if (!amount) return false;
      if (tokens.length <= 0) return false;
      let _symbol = symbol;
      if (_symbol === "XRWA") {
        _symbol = "RWA";
      }
      const token = tokens.find((t) => {
        return t.symbol === _symbol && t.chainId === _chainId;
      });

      if (!token) return false;

      const bridgeAssist = token.bridgeInfos[0];
      if (!bridgeAssist) return false;
      if (token.tokenAddress === DEFAULT_NATIVE_TOKEN_CONTRACT) return true;

      const _amount = Number(amount);
      if (Number.isNaN(_amount)) return false;

      const { data } = await getBridgeTokenAllowance(
        {
          chainId: _chainId,
          evmAddress: account ?? "",
          spenderAddress: bridgeAssist.bridgeAddress,
          tokenAddress: token.tokenAddress,
        },
        clientKey
      );

      const allowance = BigInt(data.allowance);
      const approveAmount = parseUnits(amount.toString(), token.decimal);
      if (allowance >= approveAmount) return true;

      return false;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async function approve(_chainId: ChainId, amount: number, symbol: string) {
    if (!amount) throw new Error("No amount found");
    if (tokens.length <= 0) throw new Error("No tokens found");
    if (!signer) throw new Error("No signer found");
    let _symbol = symbol;
    if (_symbol === "XRWA") {
      _symbol = "RWA";
    }
    const token = tokens.find((t) => {
      return t.symbol === _symbol && t.chainId === _chainId;
    });
    if (!token) throw new Error("No token found");
    const bridgeAssist = token.bridgeInfos[0];
    if (!bridgeAssist) throw new Error("No bridge assist found");
    const _amount = Number(amount);
    if (Number.isNaN(_amount)) throw new Error("Invalid amount");

    const tokenAddress = token.tokenAddress;

    const approveAmount = parseUnits(amount.toString(), token.decimal);

    const contract = anyToken(tokenAddress, signer);
    const tx = await contract.approve(bridgeAssist.bridgeAddress, approveAmount);

    await tx.wait(1);
    return tx;
  }

  async function send(amount: number, from: string, symbol: string) {
    if (!clientKey) throw new Error("No client key found");
    if (!account) throw new Error("No account found");

    if (!signer) throw new Error("No signer found");

    // if (!signer) return null;

    let _symbol = symbol;
    if (_symbol === "XRWA") {
      _symbol = "RWA";
    }

    const token = tokens.find((t) => {
      return t.symbol === _symbol && t.chainId === from;
    });
    if (!token) throw new Error("No token found");

    const bridgeAssist = token.bridgeInfos[0];
    if (!bridgeAssist) throw new Error("No bridge assist found");

    let address = account;

    if (!address) throw new Error("No address found");

    const contract = anyBridgeAssist(bridgeAssist.bridgeAddress, signer);

    const nonce = await contract.getUserTransactionsAmount(account);

    const index = Number(nonce);

    const amountInWei = parseUnits(amount.toString(), token.decimal);

    if (token.tokenAddress === DEFAULT_NATIVE_TOKEN_CONTRACT) {
      const tx = await contract.send(amountInWei, address, `evm.42421`, {
        value: amountInWei,
      });

      await tx.wait(1);
      const res = await addUserTransaction(
        {
          bridgeId: bridgeAssist.id,
          index: index.toString(),
          userAddress: account,
          transactionHash: tx.hash,
        },
        clientKey
      );
      return res;
    } else {
      const tx = await contract.send(amountInWei, address, `evm.42421`);

      await tx.wait(1);

      const res = await addUserTransaction(
        {
          bridgeId: bridgeAssist.id,
          index: index.toString(),
          userAddress: account,
          transactionHash: tx.hash,
        },
        clientKey
      );

      return res;
    }
  }

  async function getCurrentBlock(fromChain: string) {
    if (!clientKey) return;
    const { data } = await getCurrentBlocks({ chainIds: fromChain }, clientKey);
    return data;
  }

  async function confirmBlock(transaction: AssetChainTransaction) {
    if (!clientKey) return;
    const confirmations = transaction.confirmations;
    let isConfirmed = false;
    let attempts = 0;
    while (!isConfirmed) {
      const currentBlock = await getCurrentBlock(transaction.fromChain);
      if (!currentBlock) {
        attempts++;
        continue;
      }
      const currentBlockNumber = currentBlock[transaction.fromChain];
      if (!currentBlockNumber) {
        attempts++;
        continue;
      }
      const blockNumber = Number(currentBlockNumber);
      const difference = Number(transaction.txBlock) + confirmations - blockNumber;
      isConfirmed = Math.max(difference, 0) === 0;
      attempts++;
      if (attempts > 10) {
        throw new Error("Failed to confirm block");
      }
      await new Promise((resolve) => setTimeout(resolve, 3000));
    }
  }

  async function claimToken(transaction: AssetChainTransaction) {
    if (!isAuthenticated) {
      const session = await signIn();
      if (!session) {
        throw new Error("Sign in required to claim bridge tokens");
      }
    }

    const fromToken = tokens.find((t) => t.chainId === transaction.fromChain && t.symbol === transaction.symbol);
    if (!fromToken) throw new Error("No from token found");
    const fromBridgeAssist = fromToken.bridgeInfos[0];
    if (!fromBridgeAssist) throw new Error("No from bridge assist found");

    const { data } = await claimBridgeToken({
      bridgeId: fromBridgeAssist.id,
      fromUser: transaction.fromUser,
      index: transaction.index.toString(),
    });
    return data.txHash;
  }

  return (
    <BridgeContext.Provider
      value={{
        clientKey,
        tokens,
        balances,
        decimals,
        loadingBalances,
        getTokens,
        hasAllowance,
        getBalances,
        approve,
        send,
        confirmBlock,
        claimToken,
      }}
    >
      {children}
    </BridgeContext.Provider>
  );
};
